import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { Unit } from '../api/types';
import { useApp } from '../state/AppContext';
import { EmptyRow, ErrorBanner, ExportMenu, MultiSelect, SortableHeader, downloadCsv, today, useTableSort } from '../components/ui';

/** Everything a search can find a unit by, in one string. */
const haystack = (u: Unit) => [
  u.unitNumber, u.vin, u.plateNumber, u.plateState && u.plateNumber ? `${u.plateState} ${u.plateNumber}` : null,
  u.driver, u.region, u.home, u.routeCode, u.assignment, u.vendor, u.vendorUnitNumber, u.make, u.model, u.bodyType,
].filter(Boolean).join(' ').toLowerCase();

/**
 * Every vehicle, tractor and trailer. The one job done more than any other is
 * finding one unit fast, so the search matches on any part of a name,
 * VIN, plate, driver, route, region, home or vendor; the lists narrow by kind, status,
 * origin, region and ownership, several at once.
 */
export function Register() {
  const { reference, can, statusLabel, ownershipLabel, originName } = useApp();
  const navigate = useNavigate();
  const units = useQuery({ queryKey: ['units'], queryFn: () => api.get<Unit[]>('/api/vehicles') });

  const [search, setSearch] = useState('');
  const [classes, setClasses] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [origins, setOrigins] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [ownerships, setOwnerships] = useState<string[]>([]);
  const [reviewOnly, setReviewOnly] = useState(false);

  const all = useMemo(() => units.data ?? [], [units.data]);
  const count = (pred: (u: Unit) => boolean) => all.filter(pred).length;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((u) =>
      (classes.length === 0 || classes.includes(u.classCode))
      && (statuses.length === 0 || statuses.includes(u.status))
      && (origins.length === 0 || origins.includes(u.originCode ?? ''))
      && (regions.length === 0 || regions.includes(u.regionCode ?? NO_REGION))
      && (ownerships.length === 0 || ownerships.includes(u.ownership ?? ''))
      && (!reviewOnly || u.needsReview)
      && (!q || haystack(u).includes(q)));
  }, [all, search, classes, statuses, origins, regions, ownerships, reviewOnly]);

  const { rows: sorted, sort, onSort } = useTableSort(rows, {
    unit: (u) => u.unitNumber ?? u.vin ?? '',
    status: (u) => statusLabel(u.status),
    vehicle: (u) => vehicleOf(u),
    plate: (u) => u.plateNumber,
    ownership: (u) => [ownershipLabel(u.ownership), u.vendor].join(' '),
    region: (u) => [u.region ?? '', u.home ?? '', u.routeCode ?? ''].join(' ').trim() || null,
    driver: (u) => u.driver,
    review: (u) => (u.needsReview ? 0 : 1),
  });

  // Any of the three edit permissions opens a unit's form; only "edit" adds one.
  const canEdit = can('vehicles', 'edit') || can('vehicles', 'drivers') || can('vehicles', 'ownership');
  const canAdd = can('vehicles', 'edit');
  const columns = canEdit ? 9 : 8;
  const open = (u: Unit) => navigate(`/units/${u.fleetAssetId}`);
  const edit = (u: Unit) => navigate(`/units/${u.fleetAssetId}?edit=1`);
  const emptyText = `The register is empty.${canAdd ? ' Add a unit to start it.' : ''}`;

  const exportRows = () => downloadCsv(
    `fleet-register-${today()}.csv`,
    ['Name', 'Kind', 'Status', 'Origin', 'Year', 'Make', 'Model', 'Body', 'VIN', 'Plate state', 'Plate',
      'Ownership', 'Vendor', 'Vendor unit #', 'Region', 'Home', 'Route', 'Assignment', 'Driver', 'Odometer', 'Needs review', 'Why'],
    sorted.map((u) => [
      u.unitNumber, u.className, statusLabel(u.status), originName(u.originCode), u.modelYear, u.make, u.model, u.bodyType,
      u.vin, u.plateState, u.plateNumber, u.ownership ? ownershipLabel(u.ownership) : '', u.vendor, u.vendorUnitNumber,
      u.region, u.home, u.routeCode, u.assignment, u.driver, u.odometer, u.needsReview ? 'Yes' : '', u.reviewReasons.join(' | '),
    ]),
  );

  // Admin's regions, plus the units that have none yet so they can be found and placed.
  const regionOptions = useMemo(() => [
    ...reference.regions.map((r) => ({ value: r.regionCode, label: r.name, count: all.filter((u) => u.regionCode === r.regionCode).length })),
    { value: NO_REGION, label: '— No region yet —', count: all.filter((u) => !u.regionCode).length },
  ], [reference.regions, all]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Register</div>
          <div className="page-subtitle">Every vehicle, tractor and trailer · click a unit for its record</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportMenu onExport={exportRows} />
          {canAdd && <Link className="btn btn--primary" to="/units/new">Add a unit</Link>}
        </div>
      </div>

      {units.isError && <ErrorBanner message={(units.error as Error).message} />}

      {units.data && (
        <div className="stat-grid stat-grid--pairs" style={{ marginBottom: 14 }}>
          {reference.classes.map((c) => (
            <div key={c.classCode} className="stat stat--clickable" onClick={() => setClasses([c.classCode])}>
              <div className="stat__label">{c.name.toUpperCase()}S</div>
              <div className="stat__value">{count((u) => u.classCode === c.classCode && !['returned', 'disposed'].includes(u.status))}</div>
              <div className="stat__note">in the fleet now</div>
            </div>
          ))}
          <div className="stat stat--clickable" onClick={() => setReviewOnly(true)}>
            <div className="stat__label">NEEDS REVIEW</div>
            <div className="stat__value" style={{ color: count((u) => u.needsReview) ? 'var(--orange-text)' : undefined }}>
              {count((u) => u.needsReview)}
            </div>
            <div className="stat__note">something in the record needs a look</div>
          </div>
        </div>
      )}

      {units.data && (
        <div className="stat-grid stat-grid--pairs">
          {[
            { label: 'ACTIVE', note: 'status Active', statuses: ['active'], ownerships: [] as string[] },
            { label: 'IN THE SHOP', note: 'status In the shop', statuses: ['in_shop'], ownerships: [] },
            { label: 'RENTED', note: 'long- and short-term rentals', statuses: [] as string[], ownerships: RENTED },
            { label: 'LEASED', note: 'operating and financed leases', statuses: [], ownerships: LEASED },
          ].map((s) => (
            <div key={s.label} className="stat stat--clickable"
              onClick={() => {
                // Units that have left the fleet aren't counted, so they aren't listed either.
                setStatuses(s.statuses.length ? s.statuses : reference.statuses.map((x) => x.code).filter((c) => !GONE.includes(c)));
                setOwnerships(s.ownerships); setClasses([]); setReviewOnly(false);
              }}>
              <div className="stat__label">{s.label}</div>
              <div className="stat__value">
                {count((u) => !GONE.includes(u.status)
                  && (s.statuses.length === 0 || s.statuses.includes(u.status))
                  && (s.ownerships.length === 0 || s.ownerships.includes(u.ownership ?? '')))}
              </div>
              <div className="stat__note">{s.note}</div>
            </div>
          ))}
        </div>
      )}

      <div className="filter-row">
        <input className="control filter-row__search" placeholder="Find: name, VIN, plate, driver, route…"
          aria-label="Find a unit" value={search} onChange={(e) => setSearch(e.target.value)} />
        <MultiSelect options={reference.classes.map((c) => ({ value: c.classCode, label: c.name }))}
          selected={classes} onChange={setClasses} allLabel="All kinds" label="Kind" />
        <MultiSelect options={reference.statuses.map((s) => ({ value: s.code, label: s.label, count: count((u) => u.status === s.code) }))}
          selected={statuses} onChange={setStatuses} allLabel="Any status" label="Status" />
        <MultiSelect options={reference.origins.map((o) => ({ value: o.originCode, label: o.name }))}
          selected={origins} onChange={setOrigins} allLabel="Any origin" label="Origin" />
        <MultiSelect options={regionOptions} selected={regions} onChange={setRegions} allLabel="Any region" label="Region" />
        <MultiSelect options={reference.ownerships.map((o) => ({ value: o.code, label: o.label }))}
          selected={ownerships} onChange={setOwnerships} allLabel="Any ownership" label="Ownership" />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)} />
          Needs review only
        </label>
      </div>

      <div className="meta" style={{ marginBottom: 8 }}>
        {units.isLoading ? 'Loading the register…' : `${sorted.length} of ${all.length} units`}
      </div>

      {/* Wide screens get the table; a phone gets one card per unit, since twelve
          facts across will not fit a hand-held screen however they are squeezed. */}
      <div className="table-wrap only-wide">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="NAME" column="unit" sort={sort} onSort={onSort} />
              <SortableHeader label="STATUS" column="status" sort={sort} onSort={onSort} />
              <SortableHeader label="VEHICLE" column="vehicle" sort={sort} onSort={onSort} />
              <SortableHeader label="PLATE" column="plate" sort={sort} onSort={onSort} />
              <SortableHeader label="OWNERSHIP" column="ownership" sort={sort} onSort={onSort} />
              <SortableHeader label="REGION · ROUTE" column="region" sort={sort} onSort={onSort} />
              <SortableHeader label="DRIVER" column="driver" sort={sort} onSort={onSort} />
              <SortableHeader label="REVIEW" column="review" sort={sort} onSort={onSort} style={{ textAlign: 'center' }} />
              {canEdit && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {units.isLoading && <EmptyRow colSpan={columns}>Loading the register…</EmptyRow>}
            {units.data && all.length === 0 && <EmptyRow colSpan={columns}>{emptyText}</EmptyRow>}
            {all.length > 0 && sorted.length === 0 && <EmptyRow colSpan={columns}>No unit matches these filters.</EmptyRow>}
            {sorted.map((u) => (
              <tr key={u.fleetAssetId} className="clickable" onClick={() => open(u)}>
                <td>
                  <div className="strong" style={{ whiteSpace: 'nowrap', color: 'var(--text)' }}>
                    {u.unitNumber ?? <span className="meta">—</span>}
                  </div>
                  <div className="meta">{u.className} · {originName(u.originCode)}</div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}><StatusText status={u.status} label={statusLabel(u.status)} /></td>
                <td>
                  {vehicleOf(u) || '—'}
                  {u.vin && <div className="meta">{u.vin}</div>}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{plateOf(u) ?? '—'}</td>
                <td>
                  {u.ownership ? ownershipLabel(u.ownership) : '—'}
                  {u.vendor && <div className="meta">{u.vendor}</div>}
                </td>
                <td>
                  {u.region ?? '—'}
                  {(u.home || u.routeCode) && (
                    <div className="meta">{[u.home, u.routeCode].filter(Boolean).join(' · ')}</div>
                  )}
                </td>
                <td>{u.driver ?? '—'}</td>
                <td style={{ textAlign: 'center' }}><ReviewPill unit={u} /></td>
                {canEdit && (
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn--sm" onClick={(e) => { e.stopPropagation(); edit(u); }}>Edit</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row-cards only-narrow">
        {units.isLoading && <div className="meta">Loading the register…</div>}
        {units.data && all.length === 0 && <div className="meta">{emptyText}</div>}
        {all.length > 0 && sorted.length === 0 && <div className="meta">No unit matches these filters.</div>}
        {sorted.map((u) => (
          <div key={u.fleetAssetId} className="row-card" onClick={() => open(u)}>
            <div className="row-card__top">
              <span className="strong">{u.unitNumber ?? u.vin ?? '—'}</span>
              <StatusText status={u.status} label={statusLabel(u.status)} />
            </div>
            <div className="row-card__line">{[vehicleOf(u), u.className].filter(Boolean).join(' · ')}</div>
            <div className="row-card__line">
              {[plateOf(u), u.ownership ? ownershipLabel(u.ownership) : null, u.vendor].filter(Boolean).join(' · ') || '—'}
            </div>
            {(u.driver || u.region || u.routeCode) && (
              <div className="row-card__line">{[u.driver, u.region, u.home, u.routeCode].filter(Boolean).join(' · ')}</div>
            )}
            <div className="row-card__foot">
              <ReviewPill unit={u} withText />
              {canEdit && (
                <button className="btn btn--sm" onClick={(e) => { e.stopPropagation(); edit(u); }}>Edit</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const NO_REGION = '__none__';
const GONE = ['returned', 'disposed'];
const RENTED = ['rental_long', 'rental_short'];
const LEASED = ['operating_lease', 'financed_lease'];

const vehicleOf = (u: Unit) => [u.modelYear, u.make, u.model].filter(Boolean).join(' ');
const plateOf = (u: Unit) => (u.plateNumber ? `${u.plateState ?? '??'} ${u.plateNumber}` : null);

/** How many things need a look, with the reasons on hover. */
function ReviewPill({ unit, withText }: { unit: Unit; withText?: boolean }) {
  if (!unit.needsReview) return null;
  const n = unit.reviewReasons.length;
  return (
    <span className="pill pill--attention" title={unit.reviewReasons.join('\n')}>
      {withText ? (n ? `Needs review · ${n}` : 'Needs review') : n || '!'}
    </span>
  );
}

/** Status in the colour its meaning carries: green in use, orange needs action, grey gone. */
export function StatusText({ status, label }: { status: string; label: string }) {
  const color = ['returned', 'disposed'].includes(status) ? 'var(--muted)'
    : ['pending_return', 'pending_disposal', 'out_of_service', 'in_shop'].includes(status) ? 'var(--orange-text)'
      : 'var(--green-deep)';
  return <span style={{ color, fontWeight: 600 }}>{label}</span>;
}
